// src/App.tsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/hooks/useAuth';
import { Layout } from '@/components/layout/Layout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToastProvider } from '@/components/ui/Toast';
import { ScrollTracker } from '@/components/analytics/ScrollTracker';
import UploadPage from './pages/UploadPage';
import AnalysisPage from './pages/AnalysisPage';
import QuestionsPage from './pages/QuestionsPage';
import ResultsPage from './pages/ResultsPage';
import BillingPage from './pages/BillingPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PricingPage from './pages/PricingPage';
import HomePage from '@/pages/HomePage';
import AuthPage from '@/pages/AuthPage';
import DashboardPage from '@/pages/DashboardPage';
import NotFoundPage from '@/pages/NotFoundPage';
import SuccessPage from '@/pages/SuccessPage';
import PaymentCancelPage from '@/pages/PaymentCancelPage';
import AnalysisListPage from '@/pages/AnalysisListPage';
import ProfilePage from '@/pages/ProfilePage';
import Privacy from './components/layout/Privacy';
import Terms from './components/layout/Terms';
import YouTubePage from '@/pages/YouTubePage';
import { WorkScenariosPage } from '@/components/work-scenarios/WorkScenariosPage';
import ProHomePage from '@/pages/ProHomePage';
import OffreLancementProPage from '@/pages/OffreLancementProPage';

function App() {
  const isPro = window.location.hostname === 'pro.dpewizard.com';

  if (isPro) {
    return (
      <ErrorBoundary>
        <AuthProvider>
          <ToastProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<ProHomePage />} />
                  <Route path="auth" element={<AuthPage />} />
                  <Route path="pricing" element={<PricingPage />} />
                  <Route path="about" element={<AboutPage />} />
                  <Route path="contact" element={<ContactPage />} />
                  <Route path="success" element={<SuccessPage />} />
                  <Route path="payment/cancel" element={<PaymentCancelPage />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="privacy" element={<Privacy />} />
                  <Route path="offre-lancement-pro" element={<OffreLancementProPage />} />
                  <Route path="dashboard" element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  } />
                  <Route path="billing" element={
                    <ProtectedRoute>
                      <BillingPage />
                    </ProtectedRoute>
                  } />
                  <Route path="profile" element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  } />
                  <Route path="analysis">
                    <Route path="new" element={
                      <ProtectedRoute>
                        <UploadPage />
                      </ProtectedRoute>
                    } />
                    <Route path=":id" element={
                      <ProtectedRoute>
                        <AnalysisPage />
                      </ProtectedRoute>
                    } />
                    <Route path=":id/questions/:questionnaireId" element={
                      <ProtectedRoute>
                        <QuestionsPage />
                      </ProtectedRoute>
                    } />
                    <Route path=":id/questions" element={
                      <ProtectedRoute>
                        <QuestionsPage />
                      </ProtectedRoute>
                    } />
                    <Route path=":id/results" element={
                      <ProtectedRoute>
                        <ResultsPage />
                      </ProtectedRoute>
                    } />
                    <Route path=":id/scenarios" element={
                      <ProtectedRoute>
                        <WorkScenariosPage />
                      </ProtectedRoute>
                    } />
                    <Route path="list" element={
                      <ProtectedRoute>
                        <AnalysisListPage />
                      </ProtectedRoute>
                    } />
                  </Route>
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </ToastProvider>
        </AuthProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <ScrollTracker>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<HomePage />} />
                  <Route path="pro" element={<ProHomePage />} />
                  <Route path="auth" element={<AuthPage />} />
                  <Route path="pricing" element={<PricingPage />} />
                  <Route path="about" element={<AboutPage />} />
                  <Route path="contact" element={<ContactPage />} />
                  <Route path="success" element={<SuccessPage />} />
                  <Route path="payment/cancel" element={<PaymentCancelPage />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="privacy" element={<Privacy />} />
                  <Route path="youtube" element={<YouTubePage />} />
                  <Route path="offre-lancement-pro" element={<OffreLancementProPage />} />
                  <Route path="dashboard" element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  } />
                  <Route path="analysis">
                    <Route path="new" element={
                      <ProtectedRoute>
                        <UploadPage />
                      </ProtectedRoute>
                    } />
                    <Route path=":id" element={
                      <ProtectedRoute>
                        <AnalysisPage />
                      </ProtectedRoute>
                    } />
                    <Route path=":id/questions/:questionnaireId" element={
                      <ProtectedRoute>
                        <QuestionsPage />
                      </ProtectedRoute>
                    } />
                    <Route path=":id/questions" element={
                      <ProtectedRoute>
                        <QuestionsPage />
                      </ProtectedRoute>
                    } />
                    <Route path=":id/results" element={
                      <ProtectedRoute>
                        <ResultsPage />
                      </ProtectedRoute>
                    } />
                    <Route path=":id/scenarios" element={
                      <ProtectedRoute>
                        <WorkScenariosPage />
                      </ProtectedRoute>
                    } />
                    <Route path="list" element={
                      <ProtectedRoute>
                        <AnalysisListPage />
                      </ProtectedRoute>
                    } />
                  </Route>
                  <Route path="billing" element={
                    <ProtectedRoute>
                      <BillingPage />
                    </ProtectedRoute>
                  } />
                  <Route path="profile" element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  } />
                </Route>
                <Route path="404" element={<Layout showFooter={false} />}>
                  <Route index element={<NotFoundPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </BrowserRouter>
          </ScrollTracker>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
